import { motion } from "framer-motion";
import { Building, Megaphone, Bot, LineChart, Database, BrainCircuit, Sparkles, Magnet as MagnetIcon } from "lucide-react";
import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";

const products = [
  {
    name: "AI Lead Generation Platform",
    status: "Q3 · Private Preview",
    progress: 56,
    icon: MagnetIcon,
    color: "from-neon-magenta to-neon-purple",
    desc: "Plug-and-play acquisition engine — enrichment, qualification, routing.",
    features: ["AI lead scoring", "Live enrichment", "Auto-routing to AEs", "Slack + CRM sync", "Custom playbooks"],
    roadmap: [
      { q: "Q3", item: "Closed beta with 20 partners" },
      { q: "Q4", item: "Public preview + integrations marketplace" },
      { q: "Q1'27", item: "Multi-region launch + revenue analytics" },
    ],
  },
  {
    name: "Marketing Automation SaaS",
    status: "Q4 · Early Access",
    progress: 38,
    icon: Megaphone,
    color: "from-neon-purple to-neon-blue",
    desc: "Workflows, broadcasts and attribution — without the bloated stack.",
    features: ["Visual workflows", "Multi-channel sends", "Attribution dashboards", "AI copy drafts", "Pricing per seat"],
    roadmap: [
      { q: "Q4", item: "Design partner program" },
      { q: "Q1'27", item: "Early access launch" },
      { q: "Q2'27", item: "Public launch" },
    ],
  },
  {
    name: "Real Estate Automation Platform",
    status: "Q2 · Closed Beta",
    progress: 78,
    icon: Building,
    color: "from-neon-orange to-neon-magenta",
    desc: "End-to-end CRM and AI agents purpose-built for real estate teams.",
    cta: { label: "Explore NovaEstate", href: "https://novaestate.net/" },
    features: ["Listing CRM", "AI follow-ups", "Site visit scheduling", "WhatsApp agents", "Pipeline dashboards"],
    roadmap: [
      { q: "Q2", item: "Closed beta with 12 brokers" },
      { q: "Q3", item: "Open beta + Bangalore launch" },
      { q: "Q4", item: "India-wide rollout" },
    ],
  },
  {
    name: "Business Dashboard",
    status: "Q4 · Internal use",
    progress: 64,
    icon: LineChart,
    color: "from-neon-cyan to-emerald-400",
    desc: "One-pane-of-glass for founders — revenue, ops, marketing, AI agents.",
    features: ["Live revenue", "Channel attribution", "Agent activity feed", "Cohort retention", "Forecast view"],
    roadmap: [
      { q: "Q4", item: "Beta with portfolio clients" },
      { q: "Q1'27", item: "Standalone SaaS" },
    ],
  },
  {
    name: "AI CRM Platform",
    status: "2026 · R&D",
    progress: 28,
    icon: Database,
    color: "from-neon-blue to-neon-purple",
    desc: "A CRM that runs itself — agents do data hygiene, routing, follow-ups.",
    features: ["Auto-enrichment", "Self-cleaning records", "Smart pipelines", "Inbox copilot", "Revenue intelligence"],
    roadmap: [
      { q: "2026", item: "Research + prototypes" },
      { q: "2027", item: "Private alpha" },
    ],
  },
  {
    name: "AI Business Assistant",
    status: "2026 · R&D",
    progress: 22,
    icon: BrainCircuit,
    color: "from-neon-cyan to-neon-purple",
    desc: "A general-purpose copilot trained on your company&apos;s playbook.",
    features: ["Custom knowledge base", "Cross-tool actions", "Voice + text", "Team permissions", "Analytics"],
    roadmap: [
      { q: "2026", item: "Vertical pilots" },
      { q: "2027", item: "General availability" },
    ],
  },
];

export default function ProductsPage() {
  return (
    <div data-testid="products-root">
      <PageHero
        eyebrow="Products"
        title={<>What we&apos;re <span className="text-gradient-warm">building next.</span></>}
        subtitle="A roadmap of vertical AI products designed to plug into our growth infrastructure stack — and into your business."
        accent="warm"
      />

      <section className="container-x space-y-6 pb-12">
        {products.map((p, i) => {
          const Icon = p.icon;
          const reverse = i % 2 === 1;
          return (
            <motion.article
              key={p.name}
              data-testid={`product-card-${i}`}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.55 }}
              className="relative glass rounded-3xl p-7 sm:p-9 overflow-hidden"
            >
              <div className={`absolute -top-32 ${reverse ? "-left-32" : "-right-32"} w-96 h-96 rounded-full blur-3xl opacity-25 bg-gradient-to-tr ${p.color}`} />
              <div className={`grid grid-cols-1 lg:grid-cols-12 gap-7 ${reverse ? "lg:[direction:rtl]" : ""}`}>
                {/* Visual mockup */}
                <div className="lg:col-span-5 [direction:ltr]">
                  <ProductMockup color={p.color} icon={Icon} />
                </div>

                <div className="lg:col-span-7 [direction:ltr]">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${p.color} flex items-center justify-center`}>
                      <Icon size={20} />
                    </div>
                    <span className="font-mono text-[11px] tracking-[0.16em] uppercase text-white/70 px-2.5 py-1 rounded-full bg-white/5 border border-white/10">
                      {p.status}
                    </span>
                  </div>
                  <h2 className="mt-5 text-2xl sm:text-3xl font-bold tracking-tight">{p.name}</h2>
                  <p className="mt-2 text-white/65 max-w-xl" dangerouslySetInnerHTML={{ __html: p.desc }} />
                  {p.cta && (
                    <a
                      href={p.cta.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`product-cta-${i}`}
                      className={`mt-4 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r ${p.color} hover:shadow-[0_0_40px_rgba(255,94,0,0.35)] transition-shadow`}
                    >
                      {p.cta.label} <span aria-hidden="true">→</span>
                    </a>
                  )}

                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <p className="overline mb-2">Features</p>
                      <ul className="space-y-1.5 text-sm text-white/75">
                        {p.features.map((f) => (
                          <li key={f} className="flex items-start gap-2">
                            <Sparkles size={11} className="text-white/40 mt-1" /> {f}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="overline mb-2">Roadmap</p>
                      <ul className="space-y-2 text-sm text-white/75">
                        {p.roadmap.map((r) => (
                          <li key={r.q} className="flex items-start gap-2">
                            <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-white/45 mt-0.5 w-12 shrink-0">{r.q}</span>
                            <span>{r.item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="mt-6">
                    <div className="flex justify-between text-[11px] font-mono uppercase tracking-[0.16em] text-white/45 mb-2">
                      <span>Build progress</span>
                      <span>{p.progress}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${p.progress}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.2 }}
                        className={`h-full bg-gradient-to-r ${p.color}`}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </motion.article>
          );
        })}
      </section>

      <CTABand
        headline={<>Want early access? <span className="text-gradient-neon">Join the waitlist.</span></>}
        sub="Tell us about your business and we&apos;ll match you to the right product preview."
        primary={{ label: "Request Early Access", to: "/contact" }}
        secondary={{ label: "See AI Lab", to: "/ai-lab" }}
      />
    </div>
  );
}

function ProductMockup({ color, icon: Icon }) {
  return (
    <div className="relative rounded-2xl border border-white/10 bg-black/40 backdrop-blur p-5 overflow-hidden">
      <div className={`absolute -top-20 -right-20 w-56 h-56 rounded-full blur-3xl opacity-30 bg-gradient-to-tr ${color}`} />
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
        </div>
        <span className={`w-7 h-7 rounded-md bg-gradient-to-br ${color} flex items-center justify-center`}>
          <Icon size={14} />
        </span>
      </div>
      <div className="space-y-2.5">
        <div className="h-3 w-1/3 rounded bg-white/10" />
        <div className="h-2 w-2/3 rounded bg-white/5" />
        <div className="grid grid-cols-3 gap-2 mt-3">
          {[0,1,2].map((i) => (
            <div key={i} className="h-16 rounded-lg bg-white/[0.03] border border-white/10 p-2">
              <div className="h-1.5 w-2/3 rounded bg-white/10 mb-2" />
              <div className={`h-7 rounded bg-gradient-to-br ${color} opacity-70`} />
            </div>
          ))}
        </div>
        <div className="h-20 rounded-lg bg-white/[0.03] border border-white/10 p-3 mt-3 flex items-end gap-1">
          {[20,32,28,44,38,56,48,62,58,74,68,86,80,92].map((h, k) => (
            <span key={k} className={`flex-1 rounded-sm bg-gradient-to-t ${color}`} style={{ height: `${h}%`, opacity: 0.5 + k * 0.03 }} />
          ))}
        </div>
      </div>
    </div>
  );
}
